(function () {
  if (customElements.get('axiom-product-recommendations')) return;

  class AxiomProductRecommendations extends HTMLElement {
    connectedCallback() {
      this.bindRailControls();
      if (this.dataset.recommendationsLoaded === 'true' || this.loading) return;
      this.load();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.railAbortController?.abort();
      this.railAbortController = null;
      this.loading = false;
    }

    async load() {
      const url = this.dataset.url;
      const key = this.dataset.recommendationsKey;

      if (!url || !key) {
        this.hide();
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.loading = true;

      try {
        const response = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Recommendation request failed with status ${response.status}`);

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const fresh = Array.from(
          documentFragment.querySelectorAll('axiom-product-recommendations[data-recommendations-key]')
        ).find((element) => element.dataset.recommendationsKey === key);
        const content = fresh?.querySelector('[data-recommendations-results], [data-recommendations-preview]');

        if (!content) {
          this.hide();
          return;
        }

        this.innerHTML = fresh.innerHTML;
        this.show();
        this.dataset.recommendationsLoaded = 'true';
        this.bindRailControls();
        this.revealContent();
        this.dispatchEvent(
          new CustomEvent('axiom:recommendations:loaded', {
            bubbles: true,
            detail: { key },
          })
        );
      } catch (error) {
        if (error.name !== 'AbortError') this.hide();
      } finally {
        this.loading = false;
      }
    }

    hide() {
      (this.closest('[data-recommendations-container]') || this).hidden = true;
    }

    show() {
      (this.closest('[data-recommendations-container]') || this).hidden = false;
    }

    revealContent() {
      if (window.Axiom && typeof window.Axiom.revealElements === 'function') {
        window.Axiom.revealElements(this, {
          trigger: 'manual',
          reset: true,
          stagger: 64
        });
      }

      if (window.Axiom && typeof window.Axiom.initShareButtons === 'function') {
        window.Axiom.initShareButtons();
      }
    }

    bindRailControls() {
      this.railAbortController?.abort();
      this.railAbortController = null;

      const section = this.closest('[data-recommendations-container]');
      if (section?.dataset.recommendationsStyle !== 'horizontal_scroll') return;

      const rail = this.querySelector('[data-recommendations-rail]');
      const previousButton = section.querySelector('[data-recommendations-scroll-prev]');
      const nextButton = section.querySelector('[data-recommendations-scroll-next]');
      const indicator = section.querySelector('[data-recommendations-scroll-indicator]');
      const indicatorValue = section.querySelector('[data-recommendations-scroll-indicator-value]');

      if (!rail || !previousButton || !nextButton) return;

      const controller = new AbortController();
      const { signal } = controller;
      this.railAbortController = controller;
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

      const getScrollOffset = () => {
        const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
        if (maxScroll <= 0) return 0;
        const firstItem = rail.querySelector('.product-card, .rec-preview-card');
        if (!firstItem) return Math.max(0, Math.min(rail.scrollLeft, maxScroll));
        const railRect = rail.getBoundingClientRect();
        const itemRect = firstItem.getBoundingClientRect();
        const isRtl = window.getComputedStyle(rail).direction === 'rtl';
        const offset = isRtl ? itemRect.right - railRect.right : railRect.left - itemRect.left;
        return Math.max(0, Math.min(offset, maxScroll));
      };

      const update = () => {
        const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
        const hasOverflow = maxScroll > 1;
        const offset = getScrollOffset();
        const progress = hasOverflow
          ? Math.min(1, rail.clientWidth / rail.scrollWidth + (offset / maxScroll) * (1 - rail.clientWidth / rail.scrollWidth))
          : 1;

        previousButton.disabled = !hasOverflow || offset <= 1;
        nextButton.disabled = !hasOverflow || offset >= maxScroll - 1;
        if (indicator) indicator.hidden = !hasOverflow;
        if (indicatorValue) indicatorValue.style.setProperty('--horizontal-scroll-progress', progress.toFixed(4));
      };

      const scrollRail = (direction) => {
        const isRtl = window.getComputedStyle(rail).direction === 'rtl';
        const distance = Math.max(rail.clientWidth * 0.8, 240);
        rail.scrollBy({
          left: isRtl ? -direction * distance : direction * distance,
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      };

      previousButton.addEventListener('click', () => scrollRail(-1), { signal });
      nextButton.addEventListener('click', () => scrollRail(1), { signal });
      rail.addEventListener('scroll', update, { signal, passive: true });
      window.addEventListener('resize', update, { signal, passive: true });
      update();
    }
  }

  customElements.define('axiom-product-recommendations', AxiomProductRecommendations);
})();
